// Shared source of truth for default TOS and Privacy Policy text.
// Both LegalFooter (user-facing) and LegalContentTab (admin editor) import from here
// so they always show the same content when Firestore has no override.

export const DEFAULT_TOS = `BETOPTIMIZED LLC
Terms of Service
Effective Date: March 18, 2026

IMPORTANT — PLEASE READ CAREFULLY: By accessing or using BetOptimized, you agree to be bound by these Terms of Service. If you do not agree, do not use this platform. These Terms contain a limitation of liability and disclaimer of warranties that affect your legal rights.

1. Acceptance of Terms
By accessing, browsing, or using the BetOptimized platform, website, any associated Discord channels, or any related services (collectively, the "Platform"), you acknowledge that you have read, understood, and agree to be bound by these Terms of Service ("Terms") and our Privacy Policy. These Terms constitute a legally binding agreement between you ("User") and BetOptimized LLC, a Delaware limited liability company ("BetOptimized," "Company," "we," "us," or "our").
If you are using the Platform on behalf of an organization, you represent and warrant that you have the authority to bind that organization to these Terms.

2. Description of Services
BetOptimized provides a sports analytics platform that offers users objective mathematical data, including:
Expected value (EV) calculations for publicly available sports betting markets
Implied probability analysis and odds comparisons
Algorithmically generated wager sizing recommendations based on user-defined inputs (e.g., unit size)
Statistical analysis and data visualization related to sports betting markets
Links to third-party sportsbooks for informational reference

BetOptimized is a data and analytics service only. We do not accept wagers, operate as a sportsbook, hold user funds, or facilitate gambling transactions directly. All recommendations are mathematically derived outputs based on user-provided inputs and publicly available odds data.

3. Not Gambling Advice — Important Disclaimer
THE INFORMATION AND CALCULATIONS PROVIDED BY BETOPTIMIZED ARE FOR INFORMATIONAL AND ENTERTAINMENT PURPOSES ONLY AND DO NOT CONSTITUTE GAMBLING ADVICE, FINANCIAL ADVICE, OR A RECOMMENDATION TO PLACE ANY WAGER.

Specifically:
BetOptimized is not a licensed gambling advisor, financial advisor, or investment professional.
Wager sizing suggestions are algorithmic outputs based on user-defined unit sizes and mathematical models. They do not account for your personal financial situation, risk tolerance, jurisdiction, or any other individual factors.
Past mathematical edge or expected value does not guarantee future results. Sports betting involves significant financial risk, and you may lose money.
You are solely responsible for all betting decisions you make. BetOptimized bears no responsibility for any financial losses arising from your use of the Platform.
No representation is made that any strategy, calculation, or suggestion provided by BetOptimized will result in a profit.
Odds and lines change frequently. Always verify current odds directly with the sportsbook before placing any wager. BetOptimized is not responsible for discrepancies between displayed data and live sportsbook odds.

4. Eligibility
You may only use BetOptimized if you meet all of the following requirements:
You are at least 21 years of age (or the legal gambling age in your jurisdiction, whichever is higher).
You are located in a jurisdiction where accessing sports betting information and analytics is legal.
You are not prohibited from gambling or accessing gambling-related content under applicable law.
You are accessing the Platform for personal, non-commercial use unless otherwise agreed in writing.

By using the Platform, you represent and warrant that you meet all eligibility requirements. BetOptimized reserves the right to terminate your access if we have reason to believe you do not meet these requirements.

5. Assumption of Risk
Sports betting and sports analytics involve inherent financial risks, including the risk of significant monetary loss and the potential for gambling addiction. By using the Platform, you expressly acknowledge and agree that:
You are voluntarily assuming all risks associated with sports betting and your use of BetOptimized's analytics.
You fully understand that mathematical edge and expected value do not guarantee profitable outcomes in any individual bet or over any time period.
You release BetOptimized LLC, its members, officers, employees, and contractors from any and all claims, demands, or causes of action arising from your betting decisions or financial losses.
You are making an informed, voluntary choice to use this Platform and act on any information it provides.

6. Jurisdictional Restrictions
Online sports betting and related services are regulated differently across jurisdictions. It is your sole responsibility to determine whether accessing or using BetOptimized is legal in your jurisdiction. BetOptimized makes no representation that the Platform is appropriate or available for use in all locations.

BetOptimized does not knowingly permit access by users in jurisdictions where such access is prohibited. If you access the Platform from a restricted jurisdiction, you do so at your own risk and are solely responsible for compliance with applicable local laws.

7. Third-Party Links and Sportsbooks
The Platform may contain links to third-party sportsbooks and betting platforms. These links are provided for informational reference only. BetOptimized:
Does not endorse, recommend, or guarantee any third-party sportsbook or platform.
Has no control over the content, terms, or practices of third-party sites.
Is not responsible for any losses, damages, or disputes arising from your use of third-party platforms.
No affiliate commission or revenue will ever be earned from links.

Your use of any third-party platform is governed by that platform's own terms of service and privacy policy.

8. User Responsibilities
As a condition of using BetOptimized, you agree to:
Use the Platform only for lawful purposes and in accordance with these Terms.
Provide accurate information when setting up your account or user preferences (e.g., unit size).
Not attempt to reverse engineer, scrape, copy, or redistribute any data, calculations, or content from the Platform without written permission.
Not use the Platform in any way that could damage, disable, or impair its functionality.
Not misrepresent BetOptimized's services as guaranteed winning strategies to others.
Gamble responsibly and within your financial means. If you believe you may have a gambling problem, please seek help at the National Council on Problem Gambling (ncpgambling.org) or call 1-800-522-4700.

9. Intellectual Property
All content on the BetOptimized Platform, including but not limited to algorithms, calculations, software, design, text, graphics, and data models, is the exclusive property of BetOptimized LLC and is protected by applicable intellectual property laws.

You are granted a limited, non-exclusive, non-transferable license to access and use the Platform for personal, non-commercial purposes only. You may not reproduce, distribute, modify, create derivative works from, or commercially exploit any content from the Platform without prior written consent from BetOptimized LLC.

10. Disclaimer of Warranties
THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMITTED BY LAW, BETOPTIMIZED DISCLAIMS ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO:
IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
WARRANTIES THAT THE PLATFORM WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
WARRANTIES REGARDING THE ACCURACY, COMPLETENESS, TIMELINESS, OR RELIABILITY OF ANY DATA, CALCULATIONS, OR CONTENT ON THE PLATFORM.
WARRANTIES THAT ANY MATHEMATICAL MODEL, EXPECTED VALUE CALCULATION, OR WAGER SIZING SUGGESTION WILL RESULT IN PROFITABLE OUTCOMES.

11. Limitation of Liability
TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, BETOPTIMIZED LLC, ITS MEMBERS, OFFICERS, EMPLOYEES, CONTRACTORS, AND AGENTS SHALL NOT BE LIABLE FOR:
ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF OR INABILITY TO USE THE PLATFORM.
ANY FINANCIAL LOSSES, INCLUDING GAMBLING LOSSES, RESULTING FROM RELIANCE ON DATA, CALCULATIONS, OR SUGGESTIONS PROVIDED BY THE PLATFORM.
ANY ERRORS, INACCURACIES, OR INTERRUPTIONS IN DATA OR SERVICE.
ANY UNAUTHORIZED ACCESS TO OR USE OF OUR SERVERS OR ANY PERSONAL INFORMATION STORED THEREIN.

IN NO EVENT SHALL BETOPTIMIZED'S TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING FROM OR RELATED TO YOUR USE OF THE PLATFORM EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID TO BETOPTIMIZED IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED DOLLARS ($100.00).

California Users: Pursuant to California Civil Code Section 1668, nothing in these Terms waives liability for fraud, willful injury, or violation of law. For California residents, BetOptimized does not disclaim liability for gross negligence or willful misconduct to the extent prohibited by applicable law.

Some jurisdictions do not allow the exclusion or limitation of certain damages. In such jurisdictions, BetOptimized's liability shall be limited to the greatest extent permitted by law.

12. Indemnification
You agree to indemnify, defend, and hold harmless BetOptimized LLC and its members, officers, employees, contractors, and agents from and against any claims, liabilities, damages, losses, costs, and expenses (including reasonable attorneys' fees) arising out of or in connection with:
Your use of or access to the Platform.
Your violation of these Terms.
Your violation of any applicable law or regulation, including any gambling laws.
Any gambling losses or disputes arising from your use of third-party platforms accessed through the Platform.

13. Privacy
Your use of the Platform is also governed by our Privacy Policy, which is incorporated into these Terms by reference. By using the Platform, you consent to the collection and use of your information as described in the Privacy Policy.

14. Modifications to Terms
BetOptimized reserves the right to modify these Terms at any time. We will notify users of material changes by updating the Effective Date at the top of this document and, where appropriate, providing notice through the Platform. Your continued use of the Platform after any modifications constitutes your acceptance of the updated Terms.
We recommend reviewing these Terms periodically to stay informed of any changes.

15. Termination
BetOptimized reserves the right to suspend or terminate your access to the Platform at any time, with or without cause, and with or without notice, including if we believe you have violated these Terms or any applicable law.
Upon termination, all licenses granted to you under these Terms will immediately cease. Sections 3, 5, 9, 10, 11, 12, and 16 shall survive termination.

16. Governing Law and Dispute Resolution
These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, without regard to its conflict of law provisions.

Any dispute arising from or relating to these Terms or your use of the Platform shall be resolved exclusively through binding arbitration administered by the American Arbitration Association (AAA) under its Consumer Arbitration Rules. The arbitration shall take place in San Francisco, California. You waive any right to a jury trial or to participate in a class action lawsuit.

Notwithstanding the foregoing, BetOptimized may seek injunctive or other equitable relief in any court of competent jurisdiction to protect its intellectual property rights.

17. Responsible Gambling
BetOptimized is committed to promoting responsible gambling. If you or someone you know may have a gambling problem, resources are available:
National Council on Problem Gambling Helpline: 1-800-522-4700 (ncpgambling.org)
Gamblers Anonymous: www.gamblersanonymous.org
National Problem Gambling Helpline Chat: www.ncpgambling.org/chat

BetOptimized encourages all users to set personal limits, gamble within their financial means, and treat sports betting as entertainment rather than a source of income.

18. Miscellaneous
Entire Agreement: These Terms, together with our Privacy Policy, constitute the entire agreement between you and BetOptimized regarding your use of the Platform and supersede all prior agreements.

Severability: If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.

Waiver: BetOptimized's failure to enforce any right or provision of these Terms shall not constitute a waiver of that right or provision.

Assignment: You may not assign or transfer your rights under these Terms without BetOptimized's prior written consent. BetOptimized may assign its rights without restriction.

19. Contact Information
For questions about these Terms of Service, please contact:

BetOptimized LLC
support@betoptimized.com

By using BetOptimized, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.`;

export const DEFAULT_PRIVACY = `BETOPTIMIZED LLC
Privacy Policy
Effective Date: March 18, 2026  |  Last Updated: March 18, 2026

CALIFORNIA RESIDENTS: This Privacy Policy complies with the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA). You have specific rights regarding your personal information, including the right to know, delete, correct, and opt out of the sale or sharing of your data. See Section 10 for your full rights.

1. Introduction
BetOptimized LLC ("BetOptimized," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use our sports analytics platform, website, Discord channels, or any related services (collectively, the "Platform").

This Policy applies to all users of the Platform, including visitors, registered users, and subscribers. By using the Platform, you consent to the practices described in this Privacy Policy. If you do not agree, please do not use the Platform.

2. Information We Collect
We collect the following categories of personal information:

2.1 Information You Provide Directly
Account information: name, email address, username, and password when you register
User preferences: unit size, bankroll settings, and other inputs you provide to generate analytics
Communications: messages, feedback, or support requests you send us
Payment information: if applicable, billing details processed through our third-party payment processor (we do not store full payment card numbers)

2.2 Information Collected Automatically
Device information: IP address, browser type, operating system, and device identifiers
Usage data: pages visited, features used, time spent on the Platform, clicks, and navigation patterns
Log data: server logs, error reports, and activity timestamps
Cookies and similar technologies: see Section 6 for details

2.3 Information from Third Parties
If you connect a third-party account (e.g., Google, Discord) to register or log in, we may receive basic profile information from that service
Publicly available sports odds and betting market data from third-party data providers

3. How We Use Your Information
We use the information we collect for the following purposes:

3.1 To Provide and Improve the Platform
Generate personalized expected value calculations and wager sizing recommendations based on your inputs
Maintain and improve the Platform's functionality, accuracy, and user experience
Process transactions and manage your account
Provide customer support and respond to inquiries

3.2 Communications
Send transactional emails (account confirmation, password resets, subscription updates)
Send product updates, new feature announcements, or promotional communications (you may opt out at any time)
Respond to your questions and feedback

3.3 Analytics and Research
Analyze usage patterns to understand how users interact with the Platform
Conduct internal research to improve our mathematical models and analytics
Generate aggregated, anonymized statistics about Platform usage

3.4 Legal and Compliance
Comply with applicable laws, regulations, and legal obligations
Enforce our Terms of Service and other policies
Detect, investigate, and prevent fraudulent or illegal activity
Protect the rights, property, and safety of BetOptimized, our users, and the public

4. How We Share Your Information
We do not sell your personal information. We share your information only in the following limited circumstances:

4.1 Service Providers
We share information with trusted third-party vendors who assist us in operating the Platform, including:
Cloud hosting and infrastructure providers (e.g., AWS, Google Cloud)
Analytics providers (e.g., Google Analytics)
Payment processors (e.g., Stripe)
Email service providers
Customer support tools

These providers are contractually obligated to use your information only as directed by us and to protect it in accordance with this Policy.

4.2 Legal Requirements
We may disclose your information if required to do so by law or in response to a valid legal request, such as a subpoena, court order, or government investigation.

4.3 Business Transfers
If BetOptimized is involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you via email or prominent notice on the Platform before your information is transferred and becomes subject to a different privacy policy.

4.4 With Your Consent
We may share your information with third parties when you have given us explicit consent to do so.

4.5 Aggregated or De-Identified Data
We may share aggregated or de-identified information that cannot reasonably be used to identify you, for research, marketing, analytics, or other business purposes.

5. Data Retention
We retain your personal information for as long as necessary to fulfill the purposes described in this Policy, unless a longer retention period is required by law. Specifically:
Account information is retained for the duration of your account and for up to 3 years after account deletion
Usage and log data is retained for up to 2 years
Payment records are retained for 7 years as required by tax and accounting laws
Communications and support records are retained for up to 3 years

When your information is no longer needed, we will securely delete or anonymize it.

6. Cookies and Tracking Technologies
We use cookies and similar tracking technologies to enhance your experience on the Platform.

You can control cookies through your browser settings. Note that disabling certain cookies may affect the functionality of the Platform. To opt out of Google Analytics, visit tools.google.com/dlpage/gaoptout.

7. Data Security
We implement industry-standard technical and organizational measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. These measures include:
Encryption of data in transit using TLS/SSL
Encryption of sensitive data at rest
Access controls limiting employee access to personal data on a need-to-know basis
Regular security assessments and vulnerability testing
Incident response procedures for data breaches

However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security. In the event of a data breach affecting your rights, we will notify you as required by applicable law.

8. Children's Privacy
The Platform is not directed to individuals under the age of 21. We do not knowingly collect personal information from anyone under 21. If we become aware that we have collected personal information from a minor, we will promptly delete it. If you believe we have inadvertently collected information from a minor, please contact us immediately.

9. Third-Party Links and Services
The Platform may contain links to third-party sportsbooks and websites. This Privacy Policy applies only to BetOptimized's Platform. We are not responsible for the privacy practices of third-party sites and encourage you to review their privacy policies before providing any personal information.

10. California Privacy Rights (CCPA/CPRA)
If you are a California resident, you have the following rights under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA):

10.1 Right to Know
You have the right to request that we disclose what personal information we have collected about you, including the categories of information, the purposes for which it is used, and the categories of third parties with whom it is shared.

10.2 Right to Delete
You have the right to request that we delete personal information we have collected from you, subject to certain exceptions (e.g., where retention is required by law or necessary to complete a transaction).

10.3 Right to Correct
You have the right to request that we correct inaccurate personal information we maintain about you.

10.4 Right to Opt Out of Sale or Sharing
We do not sell your personal information. We do not share your personal information with third parties for cross-context behavioral advertising. If this changes, we will update this Policy and provide an opt-out mechanism.

10.5 Right to Limit Use of Sensitive Personal Information
To the extent we collect sensitive personal information (as defined by the CPRA), you have the right to limit its use to purposes necessary to provide the Platform.

10.6 Right to Non-Discrimination
We will not discriminate against you for exercising any of your CCPA/CPRA rights. We will not deny you services, charge different prices, or provide a different level of service based on your exercise of these rights.

10.7 How to Exercise Your Rights
To exercise any of your California privacy rights, please contact us at:
Email: support@betoptimized.com
Mail: BetOptimized LLC, 3007 California Street, Apt D, San Francisco, CA 94115

We will respond to verified requests within 45 days as required by law. We may need to verify your identity before processing your request. You may designate an authorized agent to submit requests on your behalf.

11. Nevada and Other State Privacy Rights
Nevada residents may opt out of the sale of their personal information under Nevada Revised Statutes Chapter 603A. We do not currently sell personal information, but you may submit an opt-out request to support@betoptimized.com.

Other states including Virginia, Colorado, Connecticut, and Texas have enacted privacy laws that may grant residents additional rights. We are committed to honoring applicable rights under state law. Please contact us if you have questions about your state-specific rights.

12. International Users
The Platform is operated in the United States and is intended for users located in the United States. If you access the Platform from outside the United States, your information will be transferred to and processed in the United States, where privacy laws may differ from those in your country. By using the Platform, you consent to this transfer.

13. Changes to This Privacy Policy
We may update this Privacy Policy from time to time to reflect changes in our practices, technology, or applicable law. We will notify you of material changes by:
Updating the "Last Updated" date at the top of this Policy
Sending an email notification to registered users
Posting a prominent notice on the Platform

Your continued use of the Platform after any changes constitutes your acceptance of the updated Policy. We encourage you to review this Policy periodically.

14. Contact Us
If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:

BetOptimized LLC
Attn: Privacy Officer
3007 California Street, Apt D
San Francisco, California 94115
Email: support@betoptimized.com

We will respond to all privacy-related inquiries within 30 days.

By using BetOptimized, you acknowledge that you have read and understood this Privacy Policy.`;
